import { z } from 'zod'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { zSetting } from 'app/frontend/base/helpers/forms'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import TrackingRules, {
  EditableTrackingRule,
  extendTrackingRules,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/TrackingRules'
import { useTeamTrackingRules } from 'app/frontend/base/config/queries'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import AddTeamMemberModal from '../../../TeamRoster/components/AddTeamMemberModal'
import TeamRankFormContent, { EditableTeamRank } from '../TeamRankFormContent'
import Card from 'app/frontend/base/components/Card'
import GuildDataRefreshButton from 'app/frontend/base/components/GuildDataRefreshButton'

const formSchema = z.object({
  default_team_rank_id: z.number(),
  settings: z.object({
    teamMinimumTrackLevel: zSetting(z.coerce.number().min(10).max(120)),
    rankColorPresentation: zSetting(z.string()),
  }),
  tracking_rules_attributes: z.array(
    z.object({
      id: z.number().optional(),
      rank: z.number(),
      teamRankId: z.number().nullable().optional(),
      _destroy: z.boolean().optional(),
    }),
  ),
  team_ranks_attributes: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string(),
      default: z.boolean(),
      for_alts: z.boolean(),
      wishlist_visibility: z.boolean(),
      raid_visibility: z.boolean(),
      roster_visibility: z.boolean(),
      spreadsheet_roster_visibility: z.boolean(),
      spreadsheet_summary_visibility: z.boolean(),
      spreadsheet_overview_visibility: z.boolean(),
      spreadsheet_vault_visibility: z.boolean(),
      spreadsheet_profession_visibility: z.boolean(),
      spreadsheet_raids_visibility: z.boolean(),
      color: z.string().nullable().optional(),
      _destroy: z.boolean().optional(),
    }),
  ),
})

const RosterSection = () => {
  const { team, guild } = useTeam()
  const queryClient = useQueryClient()
  const [addingMembers, setAddingMembers] = useState(false)
  const { data: trackingRules, isPending: loadingTrackingRules } =
    useTeamTrackingRules(team.id)

  if (loadingTrackingRules) return null

  return (
    <>
      {addingMembers && (
        <AddTeamMemberModal open={addingMembers} setOpen={setAddingMembers} />
      )}

      <FormWrapper<
        wowaudit.Team & {
          tracking_rules_attributes: EditableTrackingRule[]
          team_ranks_attributes: EditableTeamRank[]
          default_team_rank_id: number | undefined
        }
      >
        key={`${team?.id}-${trackingRules?.map((rule) => rule.rank).join(',')}`}
        formSchema={formSchema}
        entity={{
          ...team,
          tracking_rules_attributes: extendTrackingRules(trackingRules ?? []),
          team_ranks_attributes: team.teamRanks ?? [],
          default_team_rank_id:
            team.teamRanks?.find((rank) => rank.default)?.id ??
            team.teamRanks?.[0]?.id,
        }}
        onSave={async (form, data) => {
          const newRules = await queryClient.fetchQuery<
            wowaudit.TrackingRule[]
          >({
            queryKey: ['teamTrackingRules', { id: team?.id }],
          })

          form.reset({
            ...data,
            tracking_rules_attributes: extendTrackingRules(newRules),
            team_ranks_attributes: data.teamRanks ?? [],
            default_team_rank_id:
              data.teamRanks?.find((rank) => rank?.default)?.id ??
              data.teamRanks?.[0]?.id,
          })
        }}
        transformValues={(values) => {
          return {
            ...values,
            tracking_rules_attributes: values.tracking_rules_attributes.filter(
              (rule: EditableTrackingRule) => rule._destroy !== undefined,
            ),
            team_ranks_attributes: values.team_ranks_attributes.map(
              (rank: EditableTeamRank) => ({
                ...rank,
                color:
                  (
                    rank as EditableTeamRank & {
                      color?: string | null
                    }
                  ).color || null,
                default: rank.id === values.default_team_rank_id,
              }),
            ),
          }
        }}
        entityType='team'
      >
        <TeamRankFormContent />

        <h5>Sync members with guild</h5>
        <p className='mt-2 mb-6'>
          Characters of enabled guild ranks will be added to the team
          automatically. Your team&apos;s roster will be updated once a day (and
          every time you save changes here). Alternatively, you can add members
          to your team{' '}
          <a
            className='text-primary cursor-pointer hover:underline'
            onClick={() => setAddingMembers(true)}
          >
            manually
          </a>
          .
        </p>

        <Card className='pb-6 mb-6'>
          <div className='flex justify-end mb-2'>
            <GuildDataRefreshButton
              layout='horizontal'
              successKey='dashboard.guild.guild-update-success'
              queryToInvalidate={['guildMembers', { id: guild.id }]}
            />
          </div>
          <TrackingRules />
        </Card>

        <SettingField name='teamMinimumTrackLevel' layout='box' />
      </FormWrapper>
    </>
  )
}

export default RosterSection
